import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Filter, RefreshCw } from 'lucide-react';
import { ExceptionsResponse, reportsApi } from '../api';

const modules = [
  { value: '', label: 'All modules' },
  { value: 'attendance', label: 'Attendance' },
  { value: 'partner', label: 'Partner' },
  { value: 'inquiries', label: 'Inquiries' },
];

const rules = [
  { value: '', label: 'All rules' },
  { value: 'batch_attendance_low', label: 'Batch attendance < 75%' },
  { value: 'trainee_attendance_low', label: 'Trainee attendance < 60%' },
  { value: 'partner_completion_low', label: 'Partner completion < 60%' },
  { value: 'unresolved_inquiries', label: 'Unresolved inquiries' },
];

const entityTypes = [
  { value: '', label: 'All entities' },
  { value: 'batch', label: 'Batch' },
  { value: 'partner', label: 'Partner' },
];

const ReportsExceptions: React.FC = () => {
  const [data, setData] = useState<ExceptionsResponse['data']>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    module: '',
    rule: '',
    entityType: '',
    from: '',
    to: '',
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reportsApi.getExceptions({
        module: filters.module || undefined,
        rule: filters.rule || undefined,
        entityType: filters.entityType || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined,
      });
      setData(response.data);
    } catch (err: any) {
      console.error('Failed to load exceptions report', err);
      const message = err?.response?.data?.message ?? 'Failed to load exceptions.';
      setError(message);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    fetchData();
  };

  const handleClear = () => {
    setFilters({
      module: '',
      rule: '',
      entityType: '',
      from: '',
      to: '',
    });
    setTimeout(fetchData, 0);
  };

  const renderPayload = (row: ExceptionsResponse['data'][number]) => {
    if (!row.payload) return '-';

    if (row.rule_key === 'trainee_attendance_low' && Array.isArray(row.payload?.trainees)) {
      return (
        <div className="space-y-1">
          <div className="text-xs text-gray-500">
            Threshold: {row.payload?.threshold ?? 60}%
          </div>
          <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
            {row.payload.trainees.map((t: any) => (
              <li key={t.traineeId}>
                {t.name ?? `Trainee ${t.traineeId}`} – {Number(t.attendance ?? 0).toFixed(2)}%
              </li>
            ))}
          </ul>
        </div>
      );
    }

    if (row.rule_key === 'batch_attendance_low') {
      return (
        <div className="text-sm text-gray-700">
          Average attendance {Number(row.payload?.averageAttendance ?? 0).toFixed(2)}% (threshold {row.payload?.threshold ?? 75}%)
        </div>
      );
    }

    if (row.rule_key === 'partner_completion_low') {
      return (
        <div className="text-sm text-gray-700">
          Completion {Number(row.payload?.completionRate ?? 0).toFixed(2)}% of {row.payload?.allocated ?? 0} batches
        </div>
      );
    }

    if (row.rule_key === 'unresolved_inquiries') {
      return (
        <div className="text-sm text-gray-700">
          Unresolved inquiries: {row.payload?.unresolvedCount ?? 0}
        </div>
      );
    }

    return (
      <pre className="text-xs text-gray-600 bg-gray-50 p-2 rounded whitespace-pre-wrap">
        {JSON.stringify(row.payload, null, 2)}
      </pre>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link
            to="/dashboard/reports"
            className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Reports
          </Link>
          <h1 className="mt-3 text-2xl font-bold text-gray-900">Exception Report</h1>
          <p className="mt-1 text-sm text-gray-700">
            Daily snapshot of batches and partners breaching key operational thresholds.
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={fetchData}
          disabled={loading}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <form className="grid grid-cols-1 gap-4 sm:grid-cols-5" onSubmit={handleSubmit}>
          <div>
            <label className="form-label" htmlFor="module-filter">
              Module
            </label>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                id="module-filter"
                className="form-input pl-10"
                value={filters.module}
                onChange={(event) => setFilters((prev) => ({ ...prev, module: event.target.value }))}
              >
                {modules.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="form-label" htmlFor="rule-filter">
              Rule
            </label>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                id="rule-filter"
                className="form-input pl-10"
                value={filters.rule}
                onChange={(event) => setFilters((prev) => ({ ...prev, rule: event.target.value }))}
              >
                {rules.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="form-label" htmlFor="entity-filter">
              Entity
            </label>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                id="entity-filter"
                className="form-input pl-10"
                value={filters.entityType}
                onChange={(event) => setFilters((prev) => ({ ...prev, entityType: event.target.value }))}
              >
                {entityTypes.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="form-label" htmlFor="from-date">
              From date
            </label>
            <input
              id="from-date"
              type="date"
              className="form-input"
              value={filters.from}
              onChange={(event) => setFilters((prev) => ({ ...prev, from: event.target.value }))}
            />
          </div>

          <div>
            <label className="form-label" htmlFor="to-date">
              To date
            </label>
            <input
              id="to-date"
              type="date"
              className="form-input"
              value={filters.to}
              onChange={(event) => setFilters((prev) => ({ ...prev, to: event.target.value }))}
            />
          </div>

          <div className="sm:col-span-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button type="submit" className="btn-secondary sm:w-auto">
              Apply filters
            </button>
            <button
              type="button"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition sm:w-auto"
              onClick={handleClear}
              disabled={loading}
            >
              Clear
            </button>
          </div>
        </form>

        {error && (
          <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 mt-4">
            {error}
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-medium text-gray-900">Exceptions ({data.length})</h2>
        </div>

        {loading ? (
          <div className="px-6 py-12 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
          </div>
        ) : data.length ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detected</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Module</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rule</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 text-sm text-gray-700">
                {data.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      {new Date(row.detected_at).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="px-6 py-4 capitalize">{row.module}</td>
                    <td className="px-6 py-4">{row.rule_key}</td>
                    <td className="px-6 py-4">
                      {row.entity_type === 'batch' && row.payload?.batchCode
                        ? row.payload.batchCode
                        : row.entity_type === 'partner' && row.payload?.partnerName
                        ? row.payload.partnerName
                        : row.entity_id ?? '-'}
                    </td>
                    <td className="px-6 py-4">{renderPayload(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-6 py-12 text-center text-sm text-gray-500">
            No exceptions detected for the selected filters.
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsExceptions;
